<script setup>
/**
 * ConfirmDialog
 * Global confirmation dialog component. Renders based on shared state
 * from useConfirmDialog composable. Mount once in App.vue.
 */
import { useConfirmDialog } from '@core/composables/useConfirmDialog.js'

const { isOpen, config, handleConfirm, handleCancel } = useConfirmDialog()
</script>

<template>
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div v-if="isOpen" class="confirm-overlay" @click.self="handleCancel">
        <div class="confirm-dialog" :class="`confirm-${config.variant}`">
          <div class="confirm-header">
            <!-- Danger icon -->
            <svg
              v-if="config.variant === 'danger'"
              viewBox="0 0 24 24"
              width="28"
              height="28"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <!-- Warning icon -->
            <svg
              v-else-if="config.variant === 'warning'"
              viewBox="0 0 24 24"
              width="28"
              height="28"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <!-- Info icon -->
            <svg
              v-else
              viewBox="0 0 24 24"
              width="28"
              height="28"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <h2 class="confirm-title">{{ config.title }}</h2>
          </div>

          <div class="confirm-body">
            <p>{{ config.message }}</p>
          </div>

          <div class="confirm-footer">
            <button class="confirm-btn btn-confirm" @click="handleConfirm">
              {{ config.confirmText }}
            </button>
            <button class="confirm-btn btn-cancel" @click="handleCancel">
              {{ config.cancelText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: var(--spacing-md);
}

.confirm-dialog {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 380px;
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

/* Header variants */
.confirm-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg);
  color: white;
}

.confirm-danger .confirm-header {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.confirm-warning .confirm-header {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.confirm-info .confirm-header {
  background: var(--gradient-primary);
}

.confirm-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

/* Body */
.confirm-body {
  padding: var(--spacing-lg);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  line-height: 1.6;
}

.confirm-body p {
  margin: 0;
}

/* Footer */
.confirm-footer {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
}

.confirm-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.confirm-danger .btn-confirm {
  background: var(--color-error);
  color: white;
  border-color: var(--color-error);
}

.confirm-danger .btn-confirm:hover {
  background: #dc2626;
}

.confirm-warning .btn-confirm {
  background: var(--color-warning);
  color: white;
  border-color: var(--color-warning);
}

.confirm-warning .btn-confirm:hover {
  background: #d97706;
}

.confirm-info .btn-confirm {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.confirm-info .btn-confirm:hover {
  background: var(--color-primary-hover);
}

.btn-cancel {
  background: var(--bg-primary);
  color: var(--text-muted);
}

.btn-cancel:hover {
  background: var(--bg-secondary);
}

/* Transitions */
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}

.confirm-fade-enter-active .confirm-dialog,
.confirm-fade-leave-active .confirm-dialog {
  transition: transform 0.2s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

.confirm-fade-enter-from .confirm-dialog,
.confirm-fade-leave-to .confirm-dialog {
  transform: scale(0.95);
}

@media (max-width: 768px) {
  .confirm-dialog {
    max-width: 100%;
    margin: 0;
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    align-self: flex-end;
  }
}
</style>
